import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { istFullStr } from "@/lib/time";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, ShieldAlert } from "lucide-react";
import Logo from "@/components/Logo";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => {
    // Remember last successful server IP + port for return visits (illusion of persistent NVR connection)
    let saved = {};
    try {
      const raw = localStorage.getItem("cctv_last_server");
      if (raw) saved = JSON.parse(raw) || {};
    } catch (_) { /* ignore */ }
    return {
      server_ip: saved.server_ip || "192.168.1.100",
      port: saved.port || "8000",
      username: "",
      password: "",
    };
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(new Date());
  const [ipInvalid, setIpInvalid] = useState(false);
  const [portInvalid, setPortInvalid] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (user) {
      if (user.role === "super_admin") navigate("/super", { replace: true });
      else navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const IPV4_RE = /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d?\d)$/;
  const HOSTNAME_RE = /^(?=.{1,253}$)([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  const isValidHost = (v) => IPV4_RE.test(v.trim()) || HOSTNAME_RE.test(v.trim());
  const isValidPort = (v) => { const n = Number(v); return Number.isInteger(n) && n >= 1 && n <= 65535; };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    // Client-side format validation for illusion authenticity
    const badIp = !isValidHost(form.server_ip);
    const badPort = !isValidPort(form.port);
    setIpInvalid(badIp);
    setPortInvalid(badPort);
    if (badIp || badPort) {
      setError(badIp && badPort ? "Enter a valid server IP and port"
        : badIp ? "Enter a valid IPv4 address or hostname"
        : "Port must be a number between 1 and 65535");
      return;
    }
    setBusy(true);
    try {
      const u = await login(form);
      // Persist last-used connection details so return users see them pre-filled
      try {
        localStorage.setItem("cctv_last_server", JSON.stringify({
          server_ip: form.server_ip.trim(),
          port: String(form.port).trim(),
        }));
      } catch (_) { /* ignore */ }
      if (u.role === "super_admin") navigate("/super");
      else navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const dateStr = istFullStr(now);

  return (
    <div className="min-h-screen grid-bg text-zinc-100 flex flex-col">
      {/* Top status bar */}
      <div className="border-b border-zinc-900 bg-black/80 backdrop-blur">
        <div className="flex items-center justify-between px-6 py-2 text-[11px] uppercase tracking-widest text-zinc-500">
          <div className="flex items-center gap-3">
            <Logo size={28} />
            <span className="text-zinc-700 mono">v4.2.1</span>
          </div>
          <div data-testid="login-clock" className="mono text-zinc-400">{dateStr}</div>
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-[900px] grid md:grid-cols-[1.1fr_1fr] gap-0 border border-zinc-800 bg-zinc-950">
          {/* Left decorative */}
          <div className="hidden md:flex flex-col justify-between p-8 border-r border-zinc-800 bg-black relative overflow-hidden">
            <div className="absolute inset-0 opacity-40 pointer-events-none"
                 style={{
                   backgroundImage:
                     "linear-gradient(rgba(6,182,212,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(6,182,212,0.08) 1px, transparent 1px)",
                   backgroundSize: "16px 16px",
                 }}
            />
          <div className="relative flex items-center gap-3">
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-400/70">CCTV Management Console</div>
              <h1 className="font-display text-4xl mt-3 leading-none">
                Network<br />Video<br />Recorder
              </h1>
              <p className="mono text-xs text-zinc-500 mt-6 max-w-xs">
                Multi-camera CCTV recording and playback platform for organizations.
                Sign in with your account to view assigned channels.
              </p>
            </div>
          </div>
            <div className="relative mono text-[10px] text-zinc-600 space-y-1">
              <div>Console ID: NVR-{Math.random().toString(36).slice(2, 8).toUpperCase()}</div>
              <div>Build: v4.2.1 · MongoDB · FastAPI</div>
              <div className="flex items-center gap-2 text-emerald-500">
                <span className="w-1.5 h-1.5 bg-emerald-500 rec-blink" />
                Service online
              </div>
            </div>
          </div>

          {/* Right form */}
          <form onSubmit={onSubmit} className="p-8 flex flex-col gap-5 bg-zinc-950">
            <div className="flex items-center gap-2 text-cyan-400">
              <Lock size={16} />
              <div className="text-[11px] uppercase tracking-[0.25em]">Authenticate</div>
            </div>
            <h2 className="font-display text-2xl -mt-2">Sign in to console</h2>

            <div className="grid grid-cols-[1.5fr_1fr] gap-3">
              <div>
                <Label htmlFor="server_ip" className="text-[10px] uppercase tracking-widest text-zinc-500">Server IP</Label>
                <Input
                  id="server_ip"
                  data-testid="login-server-ip"
                  className={`mono bg-black rounded-none mt-1 ${ipInvalid ? "border-red-500 focus:border-red-500" : "border-zinc-800 focus:border-cyan-500"}`}
                  value={form.server_ip}
                  onChange={(e) => { setForm({ ...form, server_ip: e.target.value }); if (ipInvalid) setIpInvalid(false); }}
                  placeholder="192.168.1.100"
                  required
                />
              </div>
              <div>
                <Label htmlFor="port" className="text-[10px] uppercase tracking-widest text-zinc-500">Port</Label>
                <Input
                  id="port"
                  data-testid="login-port"
                  className={`mono bg-black rounded-none mt-1 ${portInvalid ? "border-red-500 focus:border-red-500" : "border-zinc-800 focus:border-cyan-500"}`}
                  value={form.port}
                  onChange={(e) => { setForm({ ...form, port: e.target.value }); if (portInvalid) setPortInvalid(false); }}
                  placeholder="8000"
                  inputMode="numeric"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="username" className="text-[10px] uppercase tracking-widest text-zinc-500">Username</Label>
              <Input
                id="username"
                data-testid="login-username"
                className="mono bg-black border-zinc-800 focus:border-cyan-500 rounded-none mt-1"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                autoComplete="username"
                required
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[10px] uppercase tracking-widest text-zinc-500">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                className="mono bg-black border-zinc-800 focus:border-cyan-500 rounded-none mt-1"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                autoComplete="current-password"
                required
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="flex items-center gap-2 border border-red-900/60 bg-red-950/30 text-red-400 text-xs px-3 py-2"
              >
                <ShieldAlert size={14} />
                <span className="mono">{error}</span>
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={busy}
              className="rounded-none bg-cyan-500 hover:bg-cyan-400 text-black font-semibold tracking-widest uppercase text-xs h-11"
            >
              {busy ? "Connecting..." : "▸ Connect"}
            </Button>

            <div className="mono text-[10px] text-zinc-600 border-t border-zinc-900 pt-3">
              Demo admin: <span className="text-zinc-400">admin</span> / <span className="text-zinc-400">admin123</span>
              <div className="text-zinc-700 mt-0.5">(change after first sign-in)</div>
            </div>
          </form>
        </div>
      </div>

      <div className="border-t border-zinc-900 bg-black">
        <div className="px-6 py-2 text-[10px] mono text-zinc-600 flex justify-between items-center">
          <Logo size={16} />
          <span>CH-01 · CH-02 · CH-03 · CH-04 · CH-05 · CH-06 · CH-07 · CH-08</span>
        </div>
      </div>
    </div>
  );
}
